
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TeacherScheduleCalendar } from "@/components/teacher/schedule/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CalendarDays, ChevronLeft, ChevronRight, Bell } from "lucide-react";
import { WeeklySchedule } from "@/components/teacher/schedule/WeeklySchedule";
import { useProfile } from "@/hooks/useProfile";
import { UpcomingNext7 } from "@/components/teacher/schedule/UpcomingNext7";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

// WeeklyItem type moved to src/types/schedule.ts

function getWeekStartISO(d: Date) {
  const day = d.getDay();
  const diffToMon = (day === 0 ? -6 : 1) - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  return monday.toISOString().slice(0, 10);
}

function dateISO(d: Date) { return d.toISOString().slice(0,10); }


export default function TeacherSchedulePage() {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState<string>("next7");
  const { profile } = useProfile();

  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : 'th-TH';

  const [newTemplates, setNewTemplates] = useState<any[]>([]);

  useEffect(() => {
    if (!profile?.teacher_id) { setNewTemplates([]); return; }
    (async () => {
      const { data } = await (supabase as any)
        .from('v_class_templates')
        .select('id, subject, weekday, start_time, end_time, created_at, start_date')
        .eq('teacher_code', profile.teacher_id)
        .eq('is_active', true);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      setNewTemplates((data ?? []).filter((t: any) => t.created_at && new Date(t.created_at) >= sevenDaysAgo));
    })();
  }, [profile?.teacher_id]);

  // Function to trigger data refresh 
  const refreshData = () => {};

  // Listen for the custom event that signals a need to refresh the data
  useEffect(() => {
    const handleRefreshEvent = () => refreshData();
    window.addEventListener('refresh-fixed-jobs', handleRefreshEvent);
    
    return () => {
      window.removeEventListener('refresh-fixed-jobs', handleRefreshEvent);
    };
  }, []);

  // Weekly tab: maintain current week start (Mon) in state
  const now = new Date();
  const day = now.getDay();
  const diffToMon = (day === 0 ? -6 : 1) - day; // convert Sun(0) handling
  const initialMonday = new Date(now);
  initialMonday.setDate(now.getDate() + diffToMon);
  const [weeklyStart, setWeeklyStart] = useState<Date>(initialMonday);
  const weeklyStartISO = useMemo(() => weeklyStart.toISOString().slice(0,10), [weeklyStart]);
  const weeklyEndISO = useMemo(() => {
    const end = new Date(weeklyStart);
    end.setDate(end.getDate() + 6);
    return end.toISOString().slice(0,10);
  }, [weeklyStart]);
  const goPrevWeek = () => {
    setWeeklyStart((d) => { const x = new Date(d); x.setDate(x.getDate() - 7); return x; });
  };
  const goNextWeek = () => {
    setWeeklyStart((d) => { const x = new Date(d); x.setDate(x.getDate() + 7); return x; });
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-2xl font-bold mb-6">{t('schedule.title')}</h1>

      {newTemplates.length > 0 && (
        <Alert className="border-green-400 bg-green-50 mb-6">
          <Bell className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">{t('schedule.newTemplatesAlert')}</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1">
              {newTemplates.map((t: any) => (
                <li key={t.id} className="text-sm text-green-700">
                  • {t.subject} — {i18n.t(`schedule.day.${t.weekday}`)} {t.start_time?.slice(0,5)}-{t.end_time?.slice(0,5)}
                  {t.start_date && (
                    <span className="ml-1">({i18n.t('schedule.starts')} {new Date(t.start_date + 'T00:00:00').toLocaleDateString(dateLocale)})</span>
                  )}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-6">
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
          <TabsList className="grid w-full max-w-xs grid-cols-3">
            <TabsTrigger value="next7" className="flex items-center gap-2">
              <span>{t('schedule.next7')}</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays size={16} />
              <span>{t('schedule.calendar')}</span>
            </TabsTrigger>
            <TabsTrigger value="weekly" className="flex items-center gap-2">
              <span>{t('schedule.weekly')}</span>
            </TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="calendar" className="block">
              <TeacherScheduleCalendar teacherCode={profile?.teacher_id} />
            </TabsContent>
            <TabsContent value="weekly">
              {profile?.teacher_id ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t('schedule.week')}: {new Date(weeklyStartISO).toLocaleDateString(dateLocale)} - {new Date(weeklyEndISO).toLocaleDateString(dateLocale)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={goPrevWeek}>
                        <ChevronLeft className="mr-1 h-4 w-4" /> {t('schedule.prevWeek')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={goNextWeek}>
                        {t('schedule.nextWeek')} <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <WeeklySchedule teacherCode={profile.teacher_id} weekStartISO={weeklyStartISO} />
                </div>
              ) : (
                <div className="text-muted-foreground">{t('schedule.noTeacherCode')}</div>
              )}
            </TabsContent>
            <TabsContent value="next7">
              <UpcomingNext7 teacherCode={profile?.teacher_id} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
